import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchReleaseStatus, type ReleaseStatusData } from "../data/release-status";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";
import { evaluateUpdateState, parseAppVersion, type UpdateLevel } from "../update/version";

// Release health (Admin) — surfaces the DEPLOYED console version, the 'web' release policy
// (latest / min-supported / force-update), how the running build evaluates against that policy,
// and a safe per-version client distribution. Read-only. No raw client identity (see
// data/release-status.ts). force-update visibility matters: it is the operator's hard-block switch.

const LEVEL_LABEL: Record<UpdateLevel, string> = {
  ok: "Up to date",
  soft: "Update available",
  hard: "Update required",
};

export function ReleaseStatus({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  currentVersion,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  currentVersion?: string;
}) {
  const { status, data, error } = useReadModel<ReleaseStatusData>(
    () => fetchReleaseStatus(client, accountId),
    [client, accountId],
    pollMs,
  );

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading release status…
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load release status."}
      </p>
    );
  }

  const deployed = parseAppVersion(currentVersion ?? __APP_VERSION__);
  const policy = data?.policy ?? null;
  const distribution = data?.distribution ?? [];

  const update = policy
    ? evaluateUpdateState({
        currentVersion: deployed.version,
        policy: {
          latestVersion: policy.latestVersion,
          minSupportedVersion: policy.minSupportedVersion,
          forceUpdate: policy.forceUpdate,
          message: policy.message,
        },
      })
    : null;

  return (
    <div className="release">
      <dl className="release__facts">
        <div className="release__fact">
          <dt>This browser build</dt>
          <dd>
            <span className="release__version">v{deployed.version || "—"}</span>
            {deployed.sha ? <span className="release__sha"> · {deployed.sha}</span> : null}
          </dd>
        </div>
        <div className="release__fact">
          <dt>Full deploy stamp</dt>
          <dd>
            <code className="release__stamp">{currentVersion ?? __APP_VERSION__}</code>
          </dd>
        </div>
        <div className="release__fact">
          <dt>Latest (web)</dt>
          <dd>{policy && policy.latestVersion ? `v${policy.latestVersion}` : "—"}</dd>
        </div>
        <div className="release__fact">
          <dt>Min supported</dt>
          <dd>{policy && policy.minSupportedVersion ? `v${policy.minSupportedVersion}` : "—"}</dd>
        </div>
        <div className="release__fact">
          <dt>Force update</dt>
          <dd>
            {policy ? (
              <span className={`release__flag release__flag--${policy.forceUpdate ? "on" : "off"}`}>
                {policy.forceUpdate ? "Active" : "Off"}
              </span>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div className="release__fact">
          <dt>Update check</dt>
          <dd>
            {update ? (
              <span className={`release__level release__level--${update.level}`}>
                {LEVEL_LABEL[update.level]}
              </span>
            ) : (
              <span className="surface__state">Release policy unavailable</span>
            )}
          </dd>
        </div>
      </dl>

      <h3 className="release__subhead">Client versions</h3>
      {distribution.length === 0 ? (
        <p className="surface__state" role="status">
          No client heartbeat yet.
        </p>
      ) : (
        <table className="release__dist">
          <thead>
            <tr>
              <th scope="col">Platform</th>
              <th scope="col">Channel</th>
              <th scope="col">Version</th>
              <th scope="col">Clients</th>
              <th scope="col">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {distribution.map((row, index) => (
              <tr key={`${row.channel}-${row.platform}-${row.appVersion}-${index}`}>
                <td>{humanizeLabel(row.platform)}</td>
                <td>{humanizeLabel(row.channel)}</td>
                <td>v{row.appVersion || "—"}</td>
                <td>{row.clientCount}</td>
                <td>{formatDisplayTime(row.lastSeenAt, timeZone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
